/**
 * "Cenário da demonstração", como no protótipo, mais a escolha do médico.
 *
 * Aciona o backend, não é visual: o que se escolhe aqui é o que o
 * reconhecedor encenado vai responder. Uma tela que fingisse a falha mentiria
 * sobre o que o servidor fez.
 *
 * Só existe no build de demonstração (`REACT_APP_WES_IDENTITY_DEMO=true`) e
 * some sozinho quando o backend não tem a superfície (404). O estado é do
 * ambiente inteiro e fica até alguém voltar para "Reconhecimento concluído" —
 * por isso a tela lê o estado real ao abrir, em vez de supor o padrão.
 */

import React, { useEffect, useState } from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getDemoState, selectDemoFace } from '../../services/identityService';

// Rótulos do protótipo para os cenários do backend (demo_face_control.py).
const SCENARIO_KEY = {
  ok: 'identity.demo.scenarios.ok',
  capture_failed: 'identity.demo.scenarios.capture_failed',
  not_recognized: 'identity.demo.scenarios.not_recognized',
  unavailable: 'identity.demo.scenarios.unavailable',
};

// "helena" -> "Helena". Os rótulos vêm de NEURO_MOCK_FACES e são nomes curtos.
const capitalize = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

export default function DemoFaceControls() {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    getDemoState()
      .then((data) => { if (alive) setState(data); })
      .catch(() => { if (alive) setState(null); });
    return () => { alive = false; };
  }, []);

  if (!state) return null;

  const change = async (patch) => {
    setSaving(true);
    setFailed(false);
    try {
      const next = await selectDemoFace(patch);
      setState((current) => ({ ...current, ...next }));
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={(theme) => ({
        display: 'grid',
        gap: 1.5,
        pt: 2,
        borderTop: `1px solid ${theme.palette.divider}`,
      })}
      data-cy="identity-demo-controls"
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
        <TextField
          select
          size="small"
          id="identityLoginSimulation"
          label={t('identity.demo.scenario')}
          value={state.scenario}
          onChange={(event) => change({ scenario: event.target.value })}
          disabled={saving}
        >
          {(state.scenarios || []).map((scenario) => (
            <MenuItem key={scenario} value={scenario}>
              {SCENARIO_KEY[scenario] ? t(SCENARIO_KEY[scenario]) : scenario}
            </MenuItem>
          ))}
        </TextField>
        {state.labels && state.labels.length > 0 ? (
          <TextField
            select
            size="small"
            id="identityLoginDemoDoctor"
            label={t('identity.demo.doctor')}
            value={state.label || ''}
            onChange={(event) => change({ label: event.target.value })}
            disabled={saving}
          >
            {state.labels.map((label) => (
              <MenuItem key={label} value={label}>{capitalize(label)}</MenuItem>
            ))}
          </TextField>
        ) : null}
      </Box>
      <Typography variant="caption" color={failed ? 'error' : 'text.secondary'}>
        {failed ? t('identity.demo.saveFailed') : t('identity.demo.sharedHint')}
      </Typography>
    </Box>
  );
}
